import { NavLink, Outlet, useOutletContext } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Boxes, FileSpreadsheet, Upload, ShieldCheck, Download, Sun, Moon, BookOpen } from "lucide-react";
import { setTheme, useTheme } from "../lib/theme";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { downloadExport, useDashboard, useRefresh, patchSettings, errMsg } from "../lib/api";
import { fmtDate } from "../lib/format";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, id: "nav-overview" },
  { to: "/stock", label: "Stock Holding", icon: Boxes, id: "nav-stock" },
  { to: "/report", label: "Amazon Report", icon: FileSpreadsheet, id: "nav-report" },
  { to: "/uploads", label: "Uploads", icon: Upload, id: "nav-uploads" },
  { to: "/admin", label: "Admin", icon: ShieldCheck, id: "nav-admin", admin: true },
];

const PeakSwitch = ({ isAdmin }) => {
  const { data } = useDashboard();
  const refresh = useRefresh();
  const peak = !!data?.settings?.peak_season;
  const toggle = async (v) => {
    try {
      await patchSettings({ peak_season: v });
      await refresh();
      toast.success(v ? "Peak season: target 28 days on hand" : "Off-peak: target 14 days on hand");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <label className="flex items-center gap-2 rounded-md border border-slate-700/60 bg-slate-900/60 px-3 py-1.5">
      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Peak</span>
      <Switch data-testid="peak-season-switch" checked={peak} onCheckedChange={toggle} disabled={!isAdmin} title={isAdmin ? "" : "Only admins can change this"} />
      <span data-testid="target-doh-label" className={`num text-xs ${peak ? "text-amber-400" : "text-sky-400"}`}>
        {peak ? 28 : 14}d
      </span>
    </label>
  );
};

const ThemeToggle = ({ theme }) => (
  <button
    data-testid="theme-toggle-btn"
    onClick={() => setTheme(theme === "light" ? "dark" : "light")}
    title={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
    className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-700 text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100"
  >
    {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
  </button>
);

export const AppShell = () => {
  const { data } = useDashboard();
  const theme = useTheme();
  const ctx = useOutletContext();
  const isAdmin = ctx?.me?.role === "admin";
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-[#0b0f17]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 sm:px-8">
          <div className="flex w-full items-center gap-3 sm:w-auto">
            <img data-testid="ep-logo" src={theme === "light" ? "/brand/ep-logo-dark.png" : "/brand/ep-logo.png"} alt="EP Group" className="h-8 w-auto sm:h-9" />
            <div className="hidden border-l border-slate-700 pl-3 font-mono text-[10px] uppercase leading-tight tracking-widest text-slate-500 sm:block">Amazon<br />Stock Control</div>
            <img data-testid="amazon-logo-mobile" src={theme === "light" ? "/brand/amazon-logo.svg" : "/brand/amazon-logo-white.svg"} alt="Amazon" className="ml-auto h-6 w-auto sm:hidden" />
          </div>
          <nav className="order-3 flex w-full gap-1 overflow-x-auto lg:order-2 lg:w-auto">
            {NAV.filter((n) => !n.admin || isAdmin).map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={id}
                className={({ isActive }) =>
                  `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
                    isActive ? "bg-sky-600/15 text-sky-300" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="order-2 ml-auto flex items-center gap-3 lg:order-3">
            <div className="hidden text-right md:block">
              <div data-testid="header-week" className="num text-xs text-slate-200">{data?.week_label || "No forecast"}</div>
              <div className="font-mono text-[10px] text-slate-500">SAP {fmtDate(data?.sap_updated_at)}</div>
            </div>
            <PeakSwitch isAdmin={isAdmin} />
            <button onClick={downloadExport} data-testid="header-export-btn" title="Download the Amazon export file" className="flex items-center gap-2 rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-500">
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">Amazon export</span>
            </button>
            <ThemeToggle theme={theme} />
            <a href="/docs/EP_Amazon_Stock_Control_User_Guide.pdf" target="_blank" rel="noreferrer" data-testid="user-guide-link" title="User guide (PDF)" className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-700 text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100">
              <BookOpen className="h-4 w-4" />
            </a>
            <div data-testid="user-menu" className="flex h-9 items-center"><UserButton /></div>
            <img data-testid="amazon-logo" src={theme === "light" ? "/brand/amazon-logo.svg" : "/brand/amazon-logo-white.svg"} alt="Amazon" className="hidden h-7 w-auto sm:block" />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1600px] px-4 py-8 sm:px-8">
        <Outlet context={ctx} />
      </main>
    </div>
  );
};
